import { supabase } from '../lib/supabase';

export const activityService = {
  /**
   * Get recent activity for a user
   */
  async getUserActivity(userId, limit = 10) {
    try {
      if (!userId) return [];

      // Fetch different types of activities in parallel
      const [orders, productReviews, sellerReviews, componentReviews, savedItems, returns] = await Promise.all([
        supabase
          .from('orders')
          .select('id, created_at, total_amount, order_number')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit),
        supabase
          .from('product_ratings')
          .select('id, created_at, rating, product_id, products(name, thumbnail_path)')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit),
        supabase
          .from('seller_ratings')
          .select('id, created_at, rating, seller_id, seller_accounts(business_name, business_slug)')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit),
        supabase
          .from('component_ratings')
          .select('id, created_at, rating, component_id, seller_components(id, custom_name, images_url, component_id, components(name, product_id))')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit),
        supabase
          .from('user_garage')
          .select('id, added_at, source, products(id, name, thumbnail_path), components(id, name, images_url)')
          .eq('user_id', userId)
          .order('added_at', { ascending: false })
          .limit(limit),
        supabase
          .from('returns')
          .select(`
            id, created_at, quantity, return_status, order_item_price, 
            order_items (
              id,
              seller_product:seller_products (
                products (name, thumbnail_path)
              ),
              seller_component:seller_components (
                images_url,
                components (name, custom_name)
              )
            )
          `)
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit)
      ]);

      const activities = [];

      // Process Orders (Purchases)
      orders.data?.forEach(order => {
        activities.push({
          id: `order-${order.id}`,
          type: 'purchase',
          title: `Order #${order.order_number}`,
          description: `Total: $${order.total_amount}`,
          timestamp: order.created_at,
          icon: 'ShoppingCart',
          image: '/assets/images/activity-fallback-1.jpg', // Placeholder
          link: `/order-confirmation/${order.order_number}`
        });
      });

      // Process Product Reviews
      productReviews.data?.forEach(review => {
        activities.push({
          id: `prod-review-${review.id}`,
          type: 'review',
          title: `Reviewed ${review.products?.name || 'Product'}`,
          description: `Left a ${review.rating}-star rating`,
          timestamp: review.created_at,
          icon: 'Star',
          image: review.products?.thumbnail_path || '/assets/images/activity-fallback-2.jpg',
          link: `/product-reviews-ratings?productId=${review.product_id}`
        });
      });

      // Process Seller Reviews
      sellerReviews.data?.forEach(review => {
        const slug = review.seller_accounts?.business_slug;
        activities.push({
          id: `seller-review-${review.id}`,
          type: 'review',
          title: `Reviewed Store: ${review.seller_accounts?.business_name || 'Seller'}`,
          description: `Left a ${review.rating}-star rating`,
          timestamp: review.created_at,
          icon: 'Star',
          image: '/assets/images/activity-fallback-3.jpg',
          link: slug ? `/store/${slug}` : '/seller-reviews-ratings'
        });
      });

      // Process Component Reviews
      componentReviews.data?.forEach(review => {
        const sc = review.seller_components;
        const componentName = sc?.custom_name || sc?.components?.name || 'Component';
        const componentImage = sc?.images_url?.[0] || '/assets/images/gear_thumbnail.png'; // local gear thumbnail fallback
        const parentProductId = sc?.components?.product_id;
        
        activities.push({
          id: `comp-review-${review.id}`,
          type: 'review',
          title: `Reviewed Component: ${componentName}`,
          description: `Left a ${review.rating}-star rating`,
          timestamp: review.created_at,
          icon: 'Star',
          image: componentImage,
          link: parentProductId ? `/product-reviews-ratings?productId=${parentProductId}&componentId=${review.component_id}` : '/model-catalog' 
        });
      });

      // Process Saved Items (user_garage)
      savedItems.data?.forEach(item => {
        const itemName = item.products?.name || item.components?.custom_name || item.components?.name || 'Item';
        const itemImage = item.products?.thumbnail_path || item.components?.images_url?.[0] || '/assets/images/gear_thumbnail.png';
        activities.push({
          id: `saved-${item.id}`,
          type: 'share', // reusing a background color that fits
          title: `Saved ${itemName}`,
          description: item.source === 'wishlist' ? 'Added to Wishlist' : 'Added to Garage',
          timestamp: item.added_at,
          icon: 'Heart',
          image: itemImage,
          link: '/user-account-dashboard?tab=wishlist'
        });
      });

      // Process Returns
      returns.data?.forEach(ret => {
        const item = ret.order_items;
        const productData = item?.seller_product?.products;
        const componentData = item?.seller_component;

        const itemName = componentData?.custom_name || componentData?.components?.name || productData?.name || 'Item';
        const itemImage = productData?.thumbnail_path || componentData?.images_url?.[0] || '/assets/images/gear_thumbnail.png';
        
        activities.push({
          id: `return-${ret.id}`,
          type: 'assembly', // reusing a color category
          title: `Started Return for ${itemName}`,
          description: `Status: ${ret.return_status}`,
          timestamp: ret.created_at,
          icon: 'RotateCcw',
          image: itemImage,
          link: '/user-account-dashboard?tab=returns'
        });
      });

      // Sort all activities by timestamp descending
      return activities
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching user activity:', error);
      return [];
    }
  },

  /**
   * Get recent activity for a seller
   */
  async getSellerActivity(sellerUserId, limit = 10) {
    try {
      if (!sellerUserId) return [];

      // Get seller account ID first
      const { data: sellerAccount } = await supabase
        .from('seller_accounts')
        .select('id')
        .eq('user_id', sellerUserId)
        .maybeSingle();

      if (!sellerAccount?.id) return [];

      const sellerId = sellerAccount.id;

      // Fetch sales, product reviews, and store reviews
      const [sales, productReviews, storeReviews] = await Promise.all([
        supabase
          .from('order_items')
          .select('id, created_at, unit_price, quantity, product_id, seller_products(products(name, thumbnail_path))')
          .eq('seller_id', sellerId)
          .order('created_at', { ascending: false })
          .limit(limit),
        supabase
          .from('product_ratings')
          .select('id, created_at, rating, product_id, products(name, thumbnail_path), users(first_name, last_name)')
          .in('product_id', 
            (await supabase.from('products').select('id').eq('created_by', sellerUserId)).data?.map(p => p.id) || []
          )
          .order('created_at', { ascending: false })
          .limit(limit),
        supabase
          .from('seller_ratings')
          .select('id, created_at, rating, users(first_name, last_name)')
          .eq('seller_id', sellerId)
          .order('created_at', { ascending: false })
          .limit(limit)
      ]);

      const activities = [];

      // Process Sales
      sales.data?.forEach(sale => {
        activities.push({
          id: `sale-${sale.id}`,
          type: 'purchase',
          title: `New Sale: ${sale.seller_products?.products?.name || 'Item'}`,
          description: `Sold ${sale.quantity} for $${(sale.unit_price * sale.quantity).toFixed(2)}`,
          timestamp: sale.created_at,
          icon: 'TrendingUp',
          image: sale.seller_products?.products?.thumbnail_path || '/assets/images/activity-fallback-1.jpg',
          link: '/seller-dashboard?tab=completedOrders'
        });
      });

      // Process Product Reviews
      productReviews.data?.forEach(review => {
        activities.push({
          id: `prod-review-${review.id}`,
          type: 'review',
          title: `New Review on ${review.products?.name || 'Product'}`,
          description: `${review.users?.first_name || 'A user'} left ${review.rating} stars`,
          timestamp: review.created_at,
          icon: 'Star',
          image: review.products?.thumbnail_path || '/assets/images/activity-fallback-2.jpg',
          link: `/product-reviews-ratings?productId=${review.product_id}`
        });
      });

      // Process Store Reviews
      storeReviews.data?.forEach(review => {
        activities.push({
          id: `store-review-${review.id}`,
          type: 'review',
          title: `New Store Review`,
          description: `${review.users?.first_name || 'A user'} left ${review.rating} stars`,
          timestamp: review.created_at,
          icon: 'Star',
          image: '/assets/images/activity-fallback-3.jpg',
          link: '/seller-dashboard?tab=storeReviews'
        });
      });

      return activities
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching seller activity:', error);
      return [];
    }
  }
};
